import React, { useMemo } from 'react';
import { KanbanBoard, ColumnDefinition } from './KanbanBoard';
import CandidateCard from './CandidateCard';
import styles from './KanbanColumn.module.css'; // Import styles for header classes
import { Candidate, KanbanColumns, KanbanColumnId } from './types';
import { ATS_CONSTANTS } from './constants';

interface ApplicationBoardProps {
  columns: KanbanColumns;
  onCardClick: (candidate: Candidate) => void;
  onScheduleClick?: (candidate: Candidate) => void;
  onGenerateOffer?: (candidate: Candidate) => void;
  onCloseJob?: () => void;
  selectedCandidates: Set<string>;
  onToggleSelection?: (id: string) => void;
  jobStatus: string;
  onUpdateStatus?: (candidate: Candidate, status: string) => void;
  onReschedule?: (candidate: Candidate) => void;
  onEditInterview?: (candidate: Candidate) => void;
  onCancelInterview?: (candidate: Candidate) => void;
}

const ApplicationBoard: React.FC<ApplicationBoardProps> = ({
  columns,
  onCardClick,
  onScheduleClick,
  onGenerateOffer,
  selectedCandidates,
  onToggleSelection,
  jobStatus,
  onUpdateStatus,
  onReschedule,
  onEditInterview,
  onCancelInterview,
}) => {
  const getHeaderClass = (columnId: string) => {
    switch (columnId) {
      case ATS_CONSTANTS.COLUMNS.APPLIED:
        return styles.kanbanColumnHeaderApplied;
      case ATS_CONSTANTS.COLUMNS.IN_CONSIDERATION:
        return styles.kanbanColumnHeaderInConsideration;
      case ATS_CONSTANTS.COLUMNS.ACCEPTED:
        return styles.kanbanColumnHeaderAccepted;
      case ATS_CONSTANTS.COLUMNS.REJECTED:
        return styles.kanbanColumnHeaderRejected;
      default:
        return '';
    }
  };

  const boardColumns: ColumnDefinition<Candidate>[] = useMemo(() => {
    return Object.values(columns).map((col) => ({
      id: col.id,
      title: col.title,
      items: col.items,
      headerClassName: getHeaderClass(col.id),
    }));
  }, [columns]);

  const renderCard = (candidate: Candidate, columnId: string) => (
    <CandidateCard
      candidate={candidate}
      columnId={columnId as KanbanColumnId}
      jobStatus={jobStatus}
      onCardClick={onCardClick}
      onGenerateOffer={onGenerateOffer}
      isSelected={selectedCandidates.has(candidate.id)}
      onToggleSelection={onToggleSelection}
      onUpdateStatus={onUpdateStatus}
      onReschedule={onReschedule}
      onEditInterview={onEditInterview}
      onCancelInterview={onCancelInterview}
    />
  );

  return (
    <KanbanBoard
      columns={boardColumns}
      renderCard={renderCard}
      variant="application"
      data-testid="ats-application-board"
    />
  );
};

export default ApplicationBoard;
